/**
 * Skeleton Loading Components
 *
 * Placeholder components that show loading state
 * while content is being fetched.
 */

import { cn } from '../../lib/utils'

interface SkeletonProps {
  className?: string
  w?: string
  h?: string
  rounded?: string
}

export function Skeleton({ className, w = '100%', h = '20px', rounded = 'rounded-[var(--radius-button)]' }: SkeletonProps) {
  return (
    <div
      className={cn('bg-background-secondary animate-pulse', rounded, className)}
      style={{ width: w, height: h }}
    />
  )
}

export function SkeletonText({ lines = 3, gap = 'gap-2' }: { lines?: number; gap?: string }) {
  return (
    <div className={cn('flex flex-col w-full', gap)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} h="16px" w={i === lines - 1 ? '70%' : '100%'} />
      ))}
    </div>
  )
}

export function SkeletonCard() {
  return (
    <div className="p-4 border border-border rounded-[var(--radius-card)]">
      <div className="flex gap-4">
        <Skeleton w="80px" h="80px" />
        <div className="flex-1 flex flex-col gap-2">
          <Skeleton w="60%" h="20px" />
          <Skeleton w="40%" h="16px" />
          <Skeleton w="80%" h="16px" />
        </div>
      </div>
    </div>
  )
}

export function SkeletonGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="flex gap-4 flex-wrap">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex-1 min-w-[280px] max-w-[400px]">
          <SkeletonCard />
        </div>
      ))}
    </div>
  )
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex px-4 py-2 bg-background-secondary rounded-[var(--radius-button)] gap-4">
        <Skeleton w="25%" h="16px" />
        <Skeleton w="20%" h="16px" />
        <Skeleton w="15%" h="16px" />
        <Skeleton w="20%" h="16px" />
        <Skeleton w="10%" h="16px" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex px-4 py-3 border border-border rounded-[var(--radius-button)] gap-4"
        >
          <Skeleton w="25%" h="16px" />
          <Skeleton w="20%" h="16px" />
          <Skeleton w="15%" h="16px" />
          <Skeleton w="20%" h="16px" />
          <Skeleton w="10%" h="16px" />
        </div>
      ))}
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="p-6">
      <Skeleton w="200px" h="32px" />
      <div className="h-4" />
      <Skeleton w="300px" h="20px" />
      <div className="h-8" />
      <div className="flex gap-4 mb-8">
        <Skeleton w="140px" h="80px" rounded="rounded-[var(--radius-card)]" />
        <Skeleton w="140px" h="80px" rounded="rounded-[var(--radius-card)]" />
        <Skeleton w="140px" h="80px" rounded="rounded-[var(--radius-card)]" />
        <Skeleton w="140px" h="80px" rounded="rounded-[var(--radius-card)]" />
      </div>
      <SkeletonGrid count={4} />
    </div>
  )
}
